import {
    ITachographEventMetadata,
    IDeviceEventOutputDto,
    IDeviceAccumulatedEventDto,
    EventTypeEnum,
    TachographDriverStatus,
} from '@quadminds/shared/tracking-system';
import { IEventOutputDto } from '../interfaces';

const getEventDuration = (metadata: ITachographEventMetadata): number => {
    const timestamp1 = +metadata.endTime;
    const timestamp2 = +metadata.startTime;
    return timestamp1 - timestamp2;
};

const getStatusEventMap = (): Record<TachographDriverStatus, EventTypeEnum> => ({
    [TachographDriverStatus.DRIVING]: EventTypeEnum.DRIVER_START_DRIVING,
    [TachographDriverStatus.WORKING]: EventTypeEnum.DRIVER_START_WORKING,
    [TachographDriverStatus.RESTING]: EventTypeEnum.DRIVER_START_RESTING,
    [TachographDriverStatus.AVAILABLE]: EventTypeEnum.DRIVER_START_AVAILABLE,
    [TachographDriverStatus.ERROR]: EventTypeEnum.DRIVER_GOES_ERROR,
    [TachographDriverStatus.NOT_AVAILABLE]: EventTypeEnum.DRIVER_GOES_NOT_AVAILABLE,
});

/**
 * Maps tachograph events for IEventOutputDto (Stork events)
 */
export const mapTachographStorkEvent = (event: IEventOutputDto): IEventOutputDto & { duration?: number } => {
    if (event.event !== EventTypeEnum.TACHOGRAPH_STATUS_CHANGE) {
        return event;
    }

    const metadata = event.metadata as ITachographEventMetadata;
    const newStatus = metadata?.newStatus;
    const startTime = metadata?.startTime;
    const endTime = metadata?.endTime;

    let specificEvent: EventTypeEnum = event.event;
    let duration = 0;

    if (newStatus && startTime && endTime) {
        duration = getEventDuration(metadata);
        const statusEventMap = getStatusEventMap();
        specificEvent = statusEventMap[newStatus] || event.event;
    }

    return {
        ...event,
        event: specificEvent,
        duration,
    };
};

/**
 * Maps tachograph events for IDeviceEventOutputDto (Device events)
 */
export const mapTachographDeviceEvent = (
    event: IDeviceEventOutputDto,
): IDeviceEventOutputDto & { duration?: number } => {
    if (event.event.code !== EventTypeEnum.TACHOGRAPH_STATUS_CHANGE) {
        return event;
    }

    const metadata = event.metadata as ITachographEventMetadata;
    const newStatus = metadata?.newStatus;
    const startTime = metadata?.startTime;
    const endTime = metadata?.endTime;

    let specificEvent: EventTypeEnum = event.event.code;
    let duration = 0;

    if (newStatus && startTime && endTime) {
        duration = getEventDuration(metadata);
        const statusEventMap = getStatusEventMap();
        specificEvent = statusEventMap[newStatus] || event.event.code;
    }

    return {
        ...event,
        event: {
            ...event.event,
            code: specificEvent,
        },
        duration,
    };
};

/**
 * Maps tachograph events for IDeviceAccumulatedEventDto (Position reports)
 */
export const mapTachographAccumulatedEvent = (event: IDeviceAccumulatedEventDto): IDeviceAccumulatedEventDto => {
    if (event.eventType !== EventTypeEnum.TACHOGRAPH_STATUS_CHANGE) {
        return event;
    }

    const metadata = event.metadata as unknown as ITachographEventMetadata;
    const newStatus = metadata?.newStatus;
    const startTime = metadata?.startTime;
    const endTime = metadata?.endTime;

    let specificEventType: EventTypeEnum = event.eventType as EventTypeEnum;
    let duration = event.metadata?.duration || 0;

    if (newStatus && startTime && endTime) {
        duration = getEventDuration(metadata);
        const statusEventMap = getStatusEventMap();
        specificEventType = statusEventMap[newStatus] || (event.eventType as EventTypeEnum);
    }

    return {
        ...event,
        eventType: specificEventType,
        metadata: {
            ...event.metadata,
            duration,
        },
    };
};

/**
 * Maps tachograph events for monitoring EventOutputDto (Monitoring events)
 */
export const mapTachographMonitoringEvent = (event: {
    eventType: string;
    metadata?: any;
    [key: string]: any;
}): { eventType: string; metadata?: any; [key: string]: any } => {
    if (event.eventType !== EventTypeEnum.TACHOGRAPH_STATUS_CHANGE) {
        return event;
    }

    const metadata = event.metadata as ITachographEventMetadata;
    const newStatus = metadata?.newStatus;

    let specificEventType: string = event.eventType;

    if (newStatus) {
        const statusEventMap = getStatusEventMap();
        specificEventType = statusEventMap[newStatus] || event.eventType;
    }

    return {
        ...event,
        eventType: specificEventType,
    };
};

/**
 * Generic function that handles all event types by delegating to specific implementations
 */
export const mapTachographEventHelpers = <
    T extends IDeviceEventOutputDto | IEventOutputDto | { eventType: string; metadata?: any; [key: string]: any },
>(
    event: T,
): T => {
    // Check if event has nested event object (IDeviceEventOutputDto)
    if ('event' in event && typeof event.event === 'object' && event.event !== null && 'code' in event.event) {
        return mapTachographDeviceEvent(event as IDeviceEventOutputDto) as T;
    }

    // Check if it's a monitoring event (has eventType as string)
    if ('eventType' in event && typeof event.eventType === 'string' && !('event' in event)) {
        return mapTachographMonitoringEvent(event as { eventType: string; metadata?: any; [key: string]: any }) as T;
    }

    // Otherwise it's IEventOutputDto
    return mapTachographStorkEvent(event as IEventOutputDto) as T;
};
